/** 대사 패널 FX — 신호 펄스·시그널 모션·엔드마커 tween 관리. */
import type Phaser from "phaser";

type SignalMotionMode = "talking" | "idle";

export type SignalMotionTarget = Phaser.GameObjects.GameObject & {
  setAlpha: (alpha: number) => SignalMotionTarget;
  setScale: (scale: number) => SignalMotionTarget;
};

export interface SignalMotionState {
  tween: Phaser.Tweens.Tween | undefined;
  mode: SignalMotionMode | undefined;
}

export function createSignalMotionState(): SignalMotionState {
  return { tween: undefined, mode: undefined };
}

export function syncSignalMotion(
  state: SignalMotionState,
  scene: Phaser.Scene,
  targets: SignalMotionTarget[],
  panelVisible: boolean,
  typewriterActive: boolean,
): void {
  if (!panelVisible) {
    state.tween?.stop();
    state.tween = undefined;
    state.mode = undefined;
    for (const target of targets) target.setAlpha(1).setScale(1);
    return;
  }
  const mode: SignalMotionMode = typewriterActive ? "talking" : "idle";
  if (state.tween?.isPlaying() && state.mode === mode) return;
  state.tween?.stop();
  state.mode = mode;
  const strong = mode === "talking";
  const alphaFrom = strong ? 0.72 : 0.38;
  const alphaTo = strong ? 1 : 0.68;
  const scaleTo = strong ? 1.13 : 1.06;
  for (const target of targets) target.setAlpha(alphaFrom).setScale(1);
  state.tween = scene.tweens.add({
    targets,
    alpha: { from: alphaFrom, to: alphaTo },
    scale: { from: 1, to: scaleTo },
    duration: strong ? 360 : 920,
    ease: "Sine.easeInOut",
    yoyo: true,
    repeat: -1,
  });
}

export interface TweenHolder {
  tween: Phaser.Tweens.Tween | undefined;
}

export function syncEndMarker(
  holder: TweenHolder,
  scene: Phaser.Scene,
  glow: Phaser.GameObjects.Arc,
  visible: boolean,
  setVisibleActive: (obj: Phaser.GameObjects.Arc, v: boolean) => void,
): void {
  if (!visible) {
    holder.tween?.stop();
    holder.tween = undefined;
    setVisibleActive(glow, false);
    glow.setAlpha(0.18).setScale(1);
    return;
  }
  setVisibleActive(glow, true);
  if (holder.tween?.isPlaying()) return;
  holder.tween = scene.tweens.add({
    targets: glow,
    alpha: { from: 0.24, to: 0.78 },
    scale: { from: 0.86, to: 1.34 },
    duration: 760,
    ease: "Sine.easeInOut",
    yoyo: true,
    repeat: -1,
  });
}

export function syncPulse(
  holder: TweenHolder,
  scene: Phaser.Scene,
  ping: Phaser.GameObjects.Arc,
  enabled: boolean,
): void {
  if (!enabled) {
    holder.tween?.stop();
    holder.tween = undefined;
    ping.setAlpha(1).setScale(1);
    return;
  }
  if (holder.tween?.isPlaying()) return;
  holder.tween = scene.tweens.add({
    targets: ping,
    alpha: { from: 1, to: 0.25 },
    scale: { from: 1, to: 1.45 },
    duration: 520,
    yoyo: true,
    repeat: -1,
  });
}
